import { useEffect, useMemo, useRef } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as cts from '../../../constants/ui/svg';
import { Table, DatePicker, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment, { Moment } from 'moment';
import JP from 'antd/es/date-picker/locale/ja_JP';
import 'moment/locale/ja';
import { useAppDispatch, useAppSelector } from 'hooks';
import { getToken } from 'helpers';
import { shopSettingActions } from 'slices/shopSettingSlice';
import { useLocation } from 'react-router-dom';
import { LoadingOutlined } from '@ant-design/icons';
import { shopListAction } from 'slices/shopSlice';
import { SelectedShop } from 'models';
import { useDimension } from 'hooks/useDimension';
moment.locale('ja');
type Props = {};
interface DataType {
  type: string;
  time: string;
  manager: string;
  action: string;
  data: string;
}
export default function Log({}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const token = getToken();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { width } = useDimension();
  const isMobile = width < 768;

  const timeType = searchParams.get('type');

  const shopLogSetting = useAppSelector((state) => state.shopSetting.shopLogSetting);
  const shopLogSettingLoading = useAppSelector((state) => state.shopSetting.shopLogSettingLoading);
  const selectShop = useAppSelector((state) => state.shopList.selectShop);
  const masterInfo = useAppSelector((state) => state.auth.masterInfo);
  const listShop = useAppSelector((state) => state.shopList.listShop);

  const [selectFilter, setSelectFilter] = useState(0);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [selectedTimeType, setSelectedTimeType] = useState(timeType || 'month');
  const [selectShopFilter, setSelectShopFilter] = useState<SelectedShop>({});

  const containerRef = useRef<HTMLDivElement | null>(null);
  const shopRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const filterSelect = [{ title: 'ALL' }, { title: t('IMAGE_UP') }, { title: t('MEMO') }];

  const isMaster = masterInfo.active && !selectShop.id;

  const stardamGroupShops = useMemo(
    () =>
      listShop
        .filter((item) => item.group_shop === 1)
        .sort((a: any, b: any) => {
          const fa = a.name.toLowerCase();
          const fb = b.name.toLowerCase();
          return fa.localeCompare(fb);
        }),
    [listShop]
  );

  const columns: ColumnsType<DataType> = [
    {
      title: t('TYPE'),
      dataIndex: 'type',
      key: 'type',
      width: isMobile ? 200 : '13%',
      render: (text) => (
        <div className="flex justify-center w-full">
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: t('TIME'),
      dataIndex: 'time',
      key: 'time',

      width: isMobile ? 200 : '15%',
      render: (text) => (
        <div className="flex justify-center w-full">
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: t('MANAGER'),
      dataIndex: 'manager',
      key: 'manager',

      width: isMobile ? 100 : '23%',
      render: (text) => (
        <div className="flex justify-center w-full">
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      dataIndex: 'action',

      width: isMobile ? 200 : '26%',
      render: (text) => (
        <div className="flex justify-center w-full">
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: t('DATA'),
      key: 'data',
      dataIndex: 'data',
      width: isMobile ? 200 : '26%',
      render: (text) => (
        <div className="flex justify-center w-full">
          <span>{text}</span>
        </div>
      ),
    },
  ];

  const findLogInMasterMode = (item: any) => {
    const findShopLog = item.user_of_shop?.find((user: any) => user.cid_shop === item.shop_id);
    return findShopLog;
  };
  const handleSwitchTab = (switchType: 'left' | 'right') => {
    const firstIndexTab = 0;
    const lastIndexTab = stardamGroupShops.length - 1;
    const findCurrentIndex = stardamGroupShops.findIndex((item) => item.id === selectShopFilter.id);

    if (findCurrentIndex !== -1) {
      if (switchType === 'left' && findCurrentIndex > firstIndexTab) {
        setSelectShopFilter(stardamGroupShops[findCurrentIndex - 1]);
      } else if (switchType === 'right' && findCurrentIndex < lastIndexTab) {
        setSelectShopFilter(stardamGroupShops[findCurrentIndex + 1]);
      }
    }
  };

  // item.type = 1 => note, lấy rank trong content
  // item.type = 2 => 30/7/2025, không cần lấy
  // item.type = 3 => lấy user of shop.nick_name
  const data: DataType[] = useMemo(() => {
    if (!Array.isArray(shopLogSetting) || shopLogSetting.length === 0) {
      return [];
    }

    return shopLogSetting.map((item: any) => {
      const findShopLog = findLogInMasterMode(item);
      const isImageType = item.type === 2 || item.type === 3;
      const typeLabel = isImageType ? t('IMAGE_UP') : t('MEMO');
      const time = moment(item.created_at).format('YYYY/MM/DD HH:mm');
      const manager = isMaster ? findShopLog?.nick_name : item.user_of_shop?.nick_name || '-';
      const action = item.log || '-';

      let dataValue = '-';
      if (item.type === 1) {
        dataValue = item?.content?.rank || '-';
      } else if (item.type === 3) {
        const staffName = item.content?.nick_name || item.content?.user_of_shop?.nick_name;
        dataValue = staffName ? `${t('STAFF')}: ${staffName}` : '-';
      }

      return {
        type: typeLabel,
        time,
        manager,
        action,
        data: dataValue,
      };
    });
  }, [shopLogSetting, t, isMaster]);

  const handleSelectTimeType = (type: 'date' | 'month') => {
    if (!shopLogSettingLoading) {
      setSelectedTimeType(type);
    }
    // 30/7/2025 if doing remember tab, uncomment this
    // if (searchParams.has('type')) {
    //   searchParams.set('type', type);
    // } else {
    //   searchParams.append('type', type);
    // }
    // const newUrl = `${location.pathname}?${searchParams}`;
    // navigate(newUrl);
  };
  const handleIncreaseOrDecreaseDatePickerButton = (type: 'increase' | 'decrease') => {
    if (selectedDate && !shopLogSettingLoading) {
      const unit = selectedTimeType == 'date' ? 'day' : 'month';
      const updatedDate =
        type === 'decrease'
          ? selectedDate.clone().subtract(1, unit)
          : selectedDate.clone().add(1, unit);
      setSelectedDate(updatedDate);
    }
  };

  // 1 note, 2 image slider, 3 image rank
  useEffect(() => {
    let filterType = [];
    switch (selectFilter) {
      case 0:
        filterType.push(1, 2, 3); //default get all
        break;
      case 1:
        filterType.push(2, 3); //get 2 image slider, 3 image rank if select up image
        break;
      case 2:
        filterType.push(1); //get note if select memo
        break;
      default:
        filterType.push(1, 2, 3); //get all (note, image slider, image rank)
        break;
    }
    const params: any = {
      type: filterType,
      date: moment(selectedDate).format('YYYY-MM-DD'),
      type_get: selectedTimeType == 'date' ? 'date' : 'month',
      shop_id: isMaster ? selectShopFilter.id : selectShop.id,
    };

    if (params.shop_id) {
      dispatch(
        shopSettingActions.getShopLogSetting({
          token: token!,
          params,
        })
      );
    }
  }, [
    dispatch,
    token,
    selectedDate,
    selectedTimeType,
    selectFilter,
    isMaster,
    selectShopFilter.id,
    selectShop.id,
  ]);
  useEffect(() => {
    if (stardamGroupShops.length > 0) {
      setSelectShopFilter(stardamGroupShops[0]);
    }
  }, [stardamGroupShops.length]);
  // fetch list shop to get stardam group shop
  useEffect(() => {
    if (token && listShop.length === 0) {
      dispatch(shopListAction.fetchShopList({ token }));
    }
  }, [dispatch, token]);
  useEffect(() => {
    if (selectShopFilter.id) {
      const el = shopRefs.current[selectShopFilter?.id];
      const container = containerRef.current;

      if (el && container) {
        el.scrollIntoView({
          behavior: 'smooth',
          inline: 'start',
          block: 'nearest',
        });
      }
    }
  }, [selectShopFilter.id]);
  return (
    <div className="flex flex-col w-full">
      {/* PC */}
      <div className="hidden justify-between items-center px-4 pt-4 w-full whitespace-nowrap lg:flex">
        <span className="text-[#252B37] text-[20px] notosans_bold">{t('LOG')}</span>
        <div className=" flex items-center border border-border-gray rounded-md h-11 px-2 w-[210px] justify-between">
          <button
            className={`h-full ${shopLogSettingLoading && 'cursor-not-allowed'}`}
            onClick={() => handleIncreaseOrDecreaseDatePickerButton('decrease')}
          >
            {cts.arrowDatePickerLeft}
          </button>
          <DatePicker
            value={selectedDate}
            picker={selectedTimeType == 'date' ? 'date' : 'month'}
            format={selectedTimeType == 'date' ? 'YYYY年M月D日' : 'YYYY年M月'}
            suffixIcon={<></>}
            locale={JP}
            allowClear={false}
            onChange={(date: Moment | null) => setSelectedDate(date || moment())}
            dropdownAlign={{ points: ['tc', 'bc'] }}
            className="rounded-md setting-log-datepicker"
          />
          <button
            className={`h-full ${shopLogSettingLoading && 'cursor-not-allowed'}`}
            onClick={() => handleIncreaseOrDecreaseDatePickerButton('increase')}
          >
            {cts.arrowDatePickerRight}
          </button>
        </div>
        <div className="border border-border-gray rounded-md h-11 w-[170px]">
          <button
            className={`rounded-l-md w-1/2 h-full border-r border-solid border-border-gray text-base ${
              selectedTimeType == 'date' && 'bg-primary text-white font-medium'
            } ${shopLogSettingLoading && 'cursor-not-allowed'}`}
            onClick={() => handleSelectTimeType('date')}
          >
            {t('DAY')}
          </button>
          <button
            onClick={() => handleSelectTimeType('month')}
            className={`rounded-r-md w-1/2 h-full text-base ${
              selectedTimeType == 'month' && 'bg-primary text-white font-medium'
            } ${shopLogSettingLoading && 'cursor-not-allowed'}`}
          >
            {t('MONTH')}
          </button>
        </div>
      </div>
      {/* Mobile */}
      <div className="block px-4 pt-4 lg:hidden">
        <div className="flex justify-between items-center">
          <span className="text-[#252B37] text-[20px] notosans_bold">{t('LOG')}</span>
          <div className="w-36 h-9 rounded-md border border-border-gray">
            <button
              className={`rounded-l-md w-1/2 h-full border-r border-solid border-border-gray text-base ${
                selectedTimeType == 'date' && 'bg-primary text-white font-medium'
              } ${shopLogSettingLoading && 'cursor-not-allowed'}`}
              onClick={() => handleSelectTimeType('date')}
            >
              {t('DAY')}
            </button>
            <button
              onClick={() => handleSelectTimeType('month')}
              className={`rounded-r-md w-1/2 h-full text-base ${
                selectedTimeType == 'month' && 'bg-primary text-white font-medium'
              } ${shopLogSettingLoading && 'cursor-not-allowed'}`}
            >
              {t('MONTH')}
            </button>
          </div>
        </div>
        <div className="flex justify-center items-center px-2 mt-2 w-full h-11 rounded-md border border-border-gray">
          <button
            className={`h-full ${shopLogSettingLoading && 'cursor-not-allowed'}`}
            onClick={() => handleIncreaseOrDecreaseDatePickerButton('decrease')}
          >
            {cts.arrowDatePickerLeft}
          </button>
          <DatePicker
            value={selectedDate}
            picker={selectedTimeType == 'date' ? 'date' : 'month'}
            format={selectedTimeType == 'date' ? 'YYYY年M月D日' : 'YYYY年M月'}
            suffixIcon={<></>}
            locale={JP}
            allowClear={false}
            onChange={(date: Moment | null) => setSelectedDate(date || moment())}
            dropdownAlign={{ points: ['tc', 'bc'] }}
            className="w-full rounded-md setting-log-datepicker"
          />
          <button
            className={`h-full ${shopLogSettingLoading && 'cursor-not-allowed'}`}
            onClick={() => handleIncreaseOrDecreaseDatePickerButton('increase')}
          >
            {cts.arrowDatePickerRight}
          </button>
        </div>
      </div>
      {isMaster && (
        <div className="flex items-center pb-2 mt-3 border-b border-border-gray">
          <button
            onClick={() => handleSwitchTab('left')}
            className="px-3"
            disabled={shopLogSettingLoading}
          >
            {cts.leftIcon}
          </button>
          <div className="flex overflow-hidden gap-2 items-center w-full" ref={containerRef}>
            {stardamGroupShops.map((shop) => {
              return (
                <button
                  key={shop.id}
                  onClick={() => setSelectShopFilter(shop)}
                  ref={(el) => (shopRefs.current[shop.id] = el)}
                  className={`${
                    selectShopFilter.id === shop.id
                      ? 'bg-primary text-white font-notosans-400'
                      : 'text-text-primary notosans_normal'
                  } border border-solid border-border-gray flex items-center w-fit h-10 text-base px-3 rounded-lg transition-all whitespace-nowrap`}
                >
                  {shop?.name}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => handleSwitchTab('right')}
            className="px-3 h-11"
            disabled={shopLogSettingLoading}
          >
            {cts.rightIcon}
          </button>
        </div>
      )}
      <div className="flex items-center lg:my-4 my-2 ml-[16px]">
        {filterSelect.map((x, y) => {
          return (
            <button
              onClick={() => setSelectFilter(y)}
              className={`${
                selectFilter === y
                  ? 'bg-primary text-white font-notosans-400'
                  : 'border border-solid border-border-gray text-text-primary notosans_normal'
              } mr-2 flex items-center w-fit h-10 text-base px-3 rounded-lg transition-all`}
            >
              {x.title}
            </button>
          );
        })}
      </div>
      <div className="overflow-auto flex-1">
        <Table
          style={{
            whiteSpace: 'nowrap',
          }}
          loading={{
            spinning: shopLogSettingLoading,
            indicator: <LoadingOutlined style={{ fontSize: 48, color: '#666687' }} spin />,
          }}
          columns={columns}
          dataSource={data}
          scroll={
            data && data.length > 0
              ? {
                  y: window.innerHeight - (80 + 24 + 200),
                  x: 'max-content',
                }
              : undefined
          }
          pagination={false}
          className="table_log"
          locale={{
            emptyText: <span>{t('NO_DATA')}</span>,
          }}
        />
      </div>
    </div>
  );
}
